import React, { useState } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth, formatError } from "@/lib/auth";

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <img src="/morrhus-logo.png" alt="Morrstofa" className="h-11 w-auto" data-testid="login-logo" />
      <div>
        <h1 className="text-xl font-semibold">Morrstofa</h1>
        <p className="text-xs text-muted-foreground">Warmachine MK4 playtest workspace</p>
      </div>
    </div>
  );
}

const useSubmit = (fn) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
};

function SignIn({ go }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit(() => login(email, password));
  return (
    <form onSubmit={run} className="space-y-4">
      <Input type="text" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
      <Input type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
      {error && <p className="text-sm text-rose-300" data-testid="login-error">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">
        {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Sign in
      </Button>
      <div className="flex justify-between text-xs">
        <button type="button" className="text-cyan-400 hover:underline" onClick={() => go("forgot")} data-testid="login-forgot-link">Forgot password?</button>
        <button type="button" className="text-cyan-400 hover:underline" onClick={() => go("signup")} data-testid="login-signup-link">Have a beta code? Sign up</button>
      </div>
    </form>
  );
}

function Forgot({ go }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const { busy, error, run } = useSubmit(async () => setSent((await api.forgotPassword(email)).message));
  if (sent) {
    return (
      <div className="space-y-4">
        <p className="text-sm" data-testid="forgot-sent">{sent} Check your inbox (and spam folder); the link works for one hour.</p>
        <Button variant="outline" className="w-full" onClick={() => go("signin")} data-testid="forgot-back-btn">Back to sign in</Button>
      </div>
    );
  }
  return (
    <form onSubmit={run} className="space-y-4">
      <p className="text-sm text-muted-foreground">Enter your account email and we'll send you a link to choose a new password.</p>
      <Input type="email" autoComplete="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
      {error && <p className="text-sm text-rose-300" data-testid="forgot-error">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-btn">
        {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Send reset link
      </Button>
    </form>
  );
}

function SignUp() {
  const { refresh } = useAuth();
  const [f, setF] = useState({ code: "", name: "", email: "", password: "", confirm: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const { busy, error, run } = useSubmit(async () => {
    if (f.password !== f.confirm) throw new Error("Passwords don't match");
    await api.signup({ code: f.code, name: f.name, email: f.email, password: f.password });
    await refresh();
  });
  return (
    <form onSubmit={run} className="space-y-3">
      <p className="text-sm text-muted-foreground">Create your account with the beta access code you were given.</p>
      <Input placeholder="Beta code, e.g. WM-AB12-CD34" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().trim() })} required className="font-mono uppercase" data-testid="signup-code-input" />
      <Input placeholder="Your name" value={f.name} onChange={set("name")} required data-testid="signup-name-input" />
      <Input type="email" autoComplete="email" placeholder="Email" value={f.email} onChange={set("email")} required data-testid="signup-email-input" />
      <Input type="password" autoComplete="new-password" placeholder="Password (8+ characters)" value={f.password} onChange={set("password")} required minLength={8} data-testid="signup-password-input" />
      <Input type="password" autoComplete="new-password" placeholder="Repeat password" value={f.confirm} onChange={set("confirm")} required data-testid="signup-confirm-input" />
      {error && <p className="text-sm text-rose-300" data-testid="signup-error">{error}</p>}
      <Button type="submit" className="w-full" disabled={busy} data-testid="signup-submit-btn">
        {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create account
      </Button>
    </form>
  );
}

export function AuthCard({ children }) {
  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="login-page">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6 space-y-4">
          <Brand />
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  const [view, setView] = useState("signin");
  return (
    <AuthCard>
      {view !== "signin" && (
        <button type="button" onClick={() => setView("signin")} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="auth-back-link">
          <ArrowLeft className="w-3 h-3" /> Sign in
        </button>
      )}
      {view === "signin" && <SignIn go={setView} />}
      {view === "forgot" && <Forgot go={setView} />}
      {view === "signup" && <SignUp />}
    </AuthCard>
  );
}
